import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Account, AccountFilters } from '@olv/ui-shared/models/account.interface';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { MERCHANT_ROLE_NAME_MAP } from '@olv/ui-shared/models/role.constants';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { MerchantAccountService } from '@olv/ui-shared/services/merchant-account.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PillDropdownComponent } from '@olv/ui-shared/ui-shared/pill-dropdown/pill-dropdown.component';
import { PillSearchComponent } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { AccountCardComponent, AddAccountComponent, AccountDetailComponent } from '../../user-management';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { VirtualScrollComponent } from '@olv/ui-shared/ui-shared/virtual-scroll/virtual-scroll.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
import { AccountFilterBase } from '../../user-management/account-filter.base';

@Component({
  selector: 'app-merchant-user-management',
  imports: [
    HasPermissionDirective,
    MatIconModule,
    PillSearchComponent,
    PillDropdownComponent,
    AccountCardComponent,
    AddAccountComponent,
    AccountDetailComponent,
    LoadingSpinnerComponent,
    VirtualScrollComponent,
    ButtonComponent,
  ],
  templateUrl: './merchant-user-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './merchant-user-management.component.scss',
})
export class MerchantUserManagementComponent extends AccountFilterBase implements OnInit, OnDestroy {
  @Input() merchantId!: string;
  @Input() page = '';
  @Output() expandUserManagement = new EventEmitter<boolean>();

  private merchantAccountService = inject(MerchantAccountService);
  private auth = inject(AuthService);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;

  get canReadAccounts(): boolean {
    return this.auth.hasPermission(Resources.MerchantAccounts, Scopes.Read);
  }

  readonly totalCount = this.merchantAccountService.totalCount;
  readonly loading = this.merchantAccountService.loading;
  readonly accounts = this.merchantAccountService.filteredAccounts;

  openAddUser = false;
  openUserDetails = false;
  selectedUserId = '';
  isMerchant = true;

  constructor() {
    super();
    effect(() => {
      const updated = this.merchantAccountService.profileUpdated();
      if (updated) {
        this.loadAccounts();
      }
    });
  }

  ngOnInit(): void {
    const filters = this.merchantAccountService.filters();

    if (!filters.status) {
      const defaultStatusOption = this.statusOptions.find((o) => o.value === this.DEFAULT_STATUS);

      if (defaultStatusOption) {
        this.selectedStatusOption = defaultStatusOption.label;
        this.merchantAccountService.setFilters({ status: defaultStatusOption.value });
      }
    } else {
      this.restoreFilterLabels(filters);
    }

    if (this.canReadAccounts) {
      this.loadInitialData();
    }
    this.isMerchant = this.page === 'edit';
  }

  ngOnDestroy(): void {
    this.merchantAccountService.clearFilters();
  }

  onAddAccount(): void {
    this.openAddUser = true;
    this.expandUserManagement.emit(this.openAddUser);
  }

  closeAddUser(): void {
    this.openAddUser = false;
    this.expandUserManagement.emit(this.openAddUser);
  }

  onAccountClick(account: Account): void {
    this.selectedUserId = account.id;
    this.openUserDetails = true;
    this.expandUserManagement.emit(this.openUserDetails);
  }

  closeUserDetails(): void {
    this.selectedUserId = '';
    this.openUserDetails = false;
    this.expandUserManagement.emit(this.openUserDetails);
  }

  reloadAccounts(): void {
    this.currentPage = 0;
    this.loadAccounts();
  }

  protected updateFilters(newFilters: Partial<AccountFilters>): void {
    this.merchantAccountService.setFilters(newFilters);
    this.currentPage = 0;
    this.loadAccounts();
  }

  private loadInitialData(): void {
    const currentFilters = this.merchantAccountService.filters();

    forkJoin({
      roles: this.merchantAccountService
        .getAccountGroups(this.merchantId)
        .pipe(catchError(() => of([] as { id: string; name: string }[]))),
      accounts: this.merchantAccountService
        .getAccounts(this.merchantId, currentFilters, this.currentPage)
        .pipe(catchError(() => of(null))),
    }).subscribe(({ roles }) => {
      this.processRoles(roles);
    });
  }

  private loadAccounts(): void {
    const currentFilters = this.merchantAccountService.filters();
    this.merchantAccountService.getAccounts(this.merchantId, currentFilters, this.currentPage).subscribe({
      error: (error) => {
        console.error('Failed to load merchant accounts:', error);
      },
    });
  }

  private processRoles(groups: { id: string; name: string }[]): void {
    const roleNameMap = MERCHANT_ROLE_NAME_MAP;

    const groupOptions = groups.map((group) => ({
      label: group.name,
      value: roleNameMap[group.name],
    }));

    groupOptions.sort((a, b) => a.value - b.value);

    this.roleOptions = [...groupOptions];

    const filters = this.merchantAccountService.filters();
    if (filters.role !== undefined) {
      const role = this.roleOptions.find((o) => o.value === filters.role);
      if (role) {
        this.selectedRoleOption = role.label;
      }
    }
  }

  private restoreFilterLabels(filters: AccountFilters): void {
    if (filters.search) {
      this.searchTerm = filters.search;
    }
    if (filters.sortBy) {
      const sort = this.sortOptions.find((o) => o.value === filters.sortBy);
      if (sort) {
        this.selectedSortOption = sort.label;
      }
    }
    if (filters.status) {
      const status = this.statusOptions.find((o) => o.value === filters.status);
      if (status) {
        this.selectedStatusOption = status.label;
      }
    }
  }
}
